import clsx from "clsx";
import React from "react";
import makeStyles from "@mui/styles/makeStyles";
import { Box, Typography } from "@mui/material";
import { colorSystem } from "../../../theme";

import {
  ExerciseSetNumberType,
  ExerciseTypeExtra,
  ExerciseTypeSet,
  Units,
} from "../../../constants";
import { Sets } from "../../workout/types";
import { useWorkoutTableStyles } from "../../workout/tableStyles";
import { hasExtraColumn } from "../../workout/utils";
import HistorySetNumberTypeMarking from "./HistorySetNumberTypeMarking";
import { unitSetLabelCoach } from "../../../utils/units";

const useStyles = makeStyles((theme) => {
  const bodyStyles = {
    fontSize: 18,
    color: colorSystem.black,
    [theme.breakpoints.down("md")]: {
      fontSize: 14,
    },
  };

  return {
    body: {
      display: "flex",
      maxHeight: 42,
      gap: 20,
      alignItems: "center",
      borderRadius: 4,
      marginBottom: 4,
    },
    setItem: {
      display: "flex",
      alignItems: "baseline",
      gap: theme.spacing(1),
    },
    bodySet: {
      fontSize: 16,
      color: colorSystem.gray,
      [theme.breakpoints.down("md")]: {
        fontSize: 14,
      },
    },
    bodyExtraColumn: bodyStyles,
    bodyReps: bodyStyles,
    bodyWeight: bodyStyles,
    bodySettings: {
      marginRight: 20,
      borderRadius: theme.spacing(0.5),
      height: theme.spacing(5),
    },
    fieldContainer: {
      display: "flex",
      justifyContent: "center",
      "& input[type=number]": {
        "-moz-appearance": "textfield",
      },

      "& input::placeholder": {
        color: theme.palette.text.disabled,
      },

      "& input:focus::placeholder": {
        color: "transparent",
      },

      "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
        {
          "-webkit-appearance": "none",
          margin: 0,
        },
    },
  };
});
export interface HistorySetClasses {
  root?: string;
  unitText?: string;
}

type WorkoutHistorySetProps = {
  set: Sets;
  setNumber: number;
  typeExtraMeasurement: ExerciseTypeExtra;
  exerciseTypeSet: ExerciseTypeSet;
  units: Units;
  className?: string;
  classes?: HistorySetClasses;
  circleClassName?: string;
};

const WorkoutHistorySet = ({
  set,
  setNumber,
  typeExtraMeasurement,
  exerciseTypeSet,
  units,
  classes,
  circleClassName,
}: WorkoutHistorySetProps) => {
  const s = useStyles();
  const tableStyles = useWorkoutTableStyles();

  const {
    extraMeasurement,
    weight,
    reps,
    type = ExerciseSetNumberType.REGULAR_SET,
    completedReps,
  } = set;

  return (
    <div
      className={clsx(
        s.body,
        classes?.root,
        hasExtraColumn(typeExtraMeasurement) && tableStyles.rowWithExtraColumn,
      )}
    >
      <div>
        <HistorySetNumberTypeMarking
          setNumber={setNumber + 1}
          type={type}
          className={circleClassName}
        />
      </div>
      <Box
        display="grid"
        gridTemplateColumns={"repeat(3, 1fr)"}
        flex={1}
        gap={2}
      >
        <Box className={s.setItem}>
          <Typography sx={{ fontWeight: 600, fontSize: 20, height: 24 }}>
            {completedReps ?? reps}
          </Typography>
          <Typography
            variant="body2"
            className={classes?.unitText}
            sx={{ color: (theme) => theme.palette.text.secondary }}
          >
            reps
          </Typography>
        </Box>

        <Box className={s.setItem}>
          <Typography sx={{ fontWeight: 600, fontSize: 20, height: 24 }}>
            {weight}
          </Typography>
          <Typography
            variant="body2"
            className={classes?.unitText}
            sx={{ color: (theme) => theme.palette.text.secondary }}
          >
            {unitSetLabelCoach(units, exerciseTypeSet)}
          </Typography>
        </Box>

        {hasExtraColumn(typeExtraMeasurement) && (
          <Box className={s.setItem}>
            <Typography sx={{ fontWeight: 600, fontSize: 20, height: 24 }}>
              {extraMeasurement || 0}
            </Typography>
            <Typography
              variant="body2"
              className={classes?.unitText}
              sx={{ color: (theme) => theme.palette.text.secondary }}
            >
              {typeExtraMeasurement}
            </Typography>
          </Box>
        )}
      </Box>
    </div>
  );
};

export default WorkoutHistorySet;
